import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useLocalCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
    // Initialize cart from localStorage so it persists across refreshes
    const [cartItems, setCartItems] = useState(() => {
        const savedCart = localStorage.getItem('eyantra_local_cart');
        return savedCart ? JSON.parse(savedCart) : [];
    });

    // Save to localStorage whenever the cart changes
    useEffect(() => {
        localStorage.setItem('eyantra_local_cart', JSON.stringify(cartItems));
    }, [cartItems]);

    const addToCart = (product, selectedVariant = null) => {
        const cartKey = selectedVariant ? `${product.id}-v${selectedVariant.id}` : String(product.id);
        setCartItems(prev => {
            const existing = prev.find(item => (item._cartKey || String(item.id)) === cartKey);
            if (existing) {
                return prev.map(item =>
                    (item._cartKey || String(item.id)) === cartKey
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            }
            return [...prev, {
                ...product,
                mrp: selectedVariant ? selectedVariant.mrp : product.mrp,
                marketOperatingPrice: selectedVariant ? null : product.marketOperatingPrice,
                marketOperatingPriceWithGst: selectedVariant ? null : product.marketOperatingPriceWithGst,
                _cartKey: cartKey,
                _variantLabel: selectedVariant?.label || null,
                quantity: 1,
            }];
        });
    };

    const updateQuantity = (cartKey, quantity) => {
        const key = String(cartKey);
        setCartItems(prev => prev.map(item =>
            (item._cartKey || String(item.id)) === key ? { ...item, quantity: Math.max(1, quantity) } : item
        ));
    };

    const removeFromCart = (cartKey) => {
        const key = String(cartKey);
        setCartItems(prev => prev.filter(item => (item._cartKey || String(item.id)) !== key));
    };

    const clearCart = () => {
        setCartItems([]);
        localStorage.removeItem('eyantra_local_cart');
    };

    const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);

    return (
        <CartContext.Provider value={{ cartItems, addToCart, updateQuantity, removeFromCart, clearCart, cartCount }}>
            {children}
        </CartContext.Provider>
    );
};